import { Bandeira, type Settings, type SettingsDefaults } from '@deizi/shared';
import { useState } from 'react';
import { AffixInput, Field, Seg } from '../../components/Form';
import { Alert } from '../../components/States';
import { errorMessage } from '../../lib/messages';
import {
  bpsInput,
  centavosInput,
  decimalInput,
  parseBps,
  parseCentavos,
  parseDecimal,
} from '../../lib/numbers';
import { useUpdateSettings } from '../../lib/settings-api';
import { StepHead, WizardFooter } from './WizardParts';

const BANDEIRA_LABELS: Record<Bandeira, string> = {
  verde: 'Verde',
  amarela: 'Amarela',
  vermelha1: 'Vermelha 1',
  vermelha2: 'Vermelha 2',
};
type Bandeira = (typeof Bandeira.options)[number];

const pick = <T,>(saved: T | null, fallback: T) => (saved === null ? fallback : saved);

/** Step 2 · Quanto custa produzir. */
export function StepCosts({
  settings,
  defaults,
  onBack,
  onNext,
}: {
  settings: Settings;
  defaults: SettingsDefaults;
  onBack: () => void;
  onNext: () => void;
}) {
  const c = settings.costs;
  const d = defaults.costs;
  const [form, setForm] = useState({
    tariff: decimalInput(pick(c.energyTariffPerKwh, d.energyTariffPerKwh), 5, 2),
    bandeira: c.bandeira,
    surcharge: c.bandeira === 'verde' ? '' : decimalInput(c.bandeiraPerKwh, 5, 3),
    labor: centavosInput(pick(c.laborRatePerHourCentavos, d.laborRatePerHourCentavos)),
    design: centavosInput(pick(c.designRatePerHourCentavos, d.designRatePerHourCentavos)),
    failure: bpsInput(pick(c.failureBps, d.failureBps)),
    waste: bpsInput(pick(c.wasteBps, d.wasteBps)),
    target: bpsInput(pick(c.targetMarginBps, d.targetMarginBps)),
    minimum: bpsInput(pick(c.minimumMarginBps, d.minimumMarginBps)),
    taxMode: pick(c.taxMode, settings.business.regime === 'simples' ? 'percent' : 'fixed'),
    das: centavosInput(c.dasMonthlyCentavos),
    taxPct: bpsInput(c.taxBps),
  });
  const update = useUpdateSettings();
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const tariff = parseDecimal(form.tariff);
  const surcharge = form.bandeira === 'verde' ? 0 : parseDecimal(form.surcharge);
  const blockMsg =
    tariff === null
      ? 'Informe a tarifa de energia para continuar.'
      : form.bandeira !== 'verde' && surcharge === null
        ? 'Informe o acréscimo da bandeira (confira na conta de luz).'
        : null;

  const save = (values: typeof form) => {
    const costs = {
      energyTariffPerKwh: parseDecimal(values.tariff),
      bandeira: values.bandeira,
      bandeiraPerKwh: values.bandeira === 'verde' ? 0 : (parseDecimal(values.surcharge) ?? 0),
      laborRatePerHourCentavos: parseCentavos(values.labor),
      designRatePerHourCentavos: parseCentavos(values.design),
      failureBps: parseBps(values.failure),
      wasteBps: parseBps(values.waste),
      targetMarginBps: parseBps(values.target),
      minimumMarginBps: parseBps(values.minimum),
      taxMode: values.taxMode,
      dasMonthlyCentavos: values.taxMode === 'fixed' ? parseCentavos(values.das) : null,
      taxBps: values.taxMode === 'percent' ? parseBps(values.taxPct) : null,
    };
    update.mutate({ costs, reason: 'Configuração inicial' }, { onSuccess: onNext });
  };

  const grid = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit,minmax(min(170px,100%),1fr))',
    gap: 14,
  } as const;
  return (
    <>
      <StepHead title="Quanto custa produzir">
        Valores sugeridos para Florianópolis (CELESC). Confira e ajuste o que for diferente.
      </StepHead>
      <section className="card col" style={{ gap: 14 }}>
        <div className="row between wrap">
          <h2>Energia</h2>
          <span className="badge">CELESC · B1 residencial</span>
        </div>
        <Field
          id="w-tariff"
          label="Tarifa com tributos"
          help="TE + TUSD com ICMS 17% (acima de 150 kWh/mês). Confira o “preço unitário com tributos” na sua conta."
        >
          <AffixInput
            id="w-tariff"
            prefix="R$"
            unit="/kWh"
            value={form.tariff}
            onChange={(v) => set('tariff', v)}
            invalid={tariff === null}
          />
        </Field>
        <Field
          label="Bandeira do mês"
          help={
            form.bandeira === 'verde'
              ? 'Guardada à parte, mês a mês. Verde não soma nada.'
              : 'Acréscimo por kWh, com tributos.'
          }
        >
          <Seg
            label="Bandeira"
            value={form.bandeira}
            onChange={(v) => {
              const suggestion = defaults.bandeiraSuggestions[v];
              setForm((f) => ({
                ...f,
                bandeira: v,
                surcharge:
                  v === 'verde' || suggestion === null ? '' : decimalInput(suggestion, 5, 3),
              }));
            }}
            options={Bandeira.options.map((b) => ({ value: b, label: BANDEIRA_LABELS[b] }))}
          />
        </Field>
        {form.bandeira !== 'verde' ? (
          <div style={{ maxWidth: 260 }}>
            <Field id="w-surcharge" label="Acréscimo da bandeira">
              <AffixInput
                id="w-surcharge"
                prefix="+ R$"
                unit="/kWh"
                placeholder="confira na conta"
                value={form.surcharge}
                onChange={(v) => set('surcharge', v)}
              />
            </Field>
          </div>
        ) : null}
      </section>
      <section className="card col" style={{ gap: 14 }}>
        <h2>Mão de obra e perdas</h2>
        <div style={grid}>
          <Field id="w-labor" label="Acabamento e embalagem">
            <AffixInput
              id="w-labor"
              prefix="R$"
              unit="/hora"
              value={form.labor}
              onChange={(v) => set('labor', v)}
            />
          </Field>
          <Field id="w-design" label="Projeto (modelagem sob encomenda)">
            <AffixInput
              id="w-design"
              prefix="R$"
              unit="/hora"
              value={form.design}
              onChange={(v) => set('design', v)}
            />
          </Field>
          <Field id="w-fail" label="Taxa de falha">
            <AffixInput
              id="w-fail"
              unit="%"
              value={form.failure}
              onChange={(v) => set('failure', v)}
            />
          </Field>
          <Field id="w-waste" label="Desperdício de filamento">
            <AffixInput
              id="w-waste"
              unit="%"
              value={form.waste}
              onChange={(v) => set('waste', v)}
            />
          </Field>
          <Field id="w-target" label="Margem alvo">
            <AffixInput
              id="w-target"
              unit="%"
              value={form.target}
              onChange={(v) => set('target', v)}
            />
          </Field>
          <Field id="w-min" label="Margem mínima" help="Abaixo disso, o preço fica vermelho.">
            <AffixInput
              id="w-min"
              unit="%"
              value={form.minimum}
              onChange={(v) => set('minimum', v)}
            />
          </Field>
        </div>
        {(parseBps(form.minimum) ?? 0) > (parseBps(form.target) ?? 0) ? (
          <Alert tone="warn">
            A margem mínima está maior que a margem alvo. Todo preço sugerido vai aparecer vermelho.
          </Alert>
        ) : null}
      </section>
      <section className="card col" style={{ gap: 14 }}>
        <h2>Imposto</h2>
        <Seg
          label="Forma do imposto"
          value={form.taxMode}
          onChange={(v) => set('taxMode', v)}
          options={[
            { value: 'fixed', label: 'Valor fixo por mês (MEI)' },
            { value: 'percent', label: '% do faturamento (Simples)' },
          ]}
        />
        <div style={{ maxWidth: 260 }}>
          {form.taxMode === 'fixed' ? (
            <Field
              id="w-das"
              label="DAS mensal"
              help="Entra nos custos fixos do mês, não no preço de cada peça."
            >
              <AffixInput
                id="w-das"
                prefix="R$"
                placeholder="Copie do boleto do DAS"
                value={form.das}
                onChange={(v) => set('das', v)}
              />
            </Field>
          ) : (
            <Field
              id="w-tax"
              label="Alíquota efetiva"
              help="Somada às tarifas do canal em cada preço sugerido. Peça o número ao seu contador."
            >
              <AffixInput
                id="w-tax"
                unit="%"
                value={form.taxPct}
                onChange={(v) => set('taxPct', v)}
              />
            </Field>
          )}
        </div>
      </section>
      {update.isError ? <Alert tone="err">{errorMessage(update.error)}</Alert> : null}
      <WizardFooter
        busy={update.isPending}
        blockMsg={blockMsg}
        onBack={onBack}
        onSuggest={() =>
          save({
            ...form,
            tariff: decimalInput(d.energyTariffPerKwh, 5, 2),
            bandeira: 'verde',
            surcharge: '',
            labor: centavosInput(d.laborRatePerHourCentavos),
            design: centavosInput(d.designRatePerHourCentavos),
            failure: bpsInput(d.failureBps),
            waste: bpsInput(d.wasteBps),
            target: bpsInput(d.targetMarginBps),
            minimum: bpsInput(d.minimumMarginBps),
          })
        }
        onContinue={() => save(form)}
      />
    </>
  );
}
